import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { credentialsService } from "@/api/credentials";

const CredentialsSettings = () => {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        companyName: "",
        address: "",
        phoneNumber: "",
        taxId: ""
    });

    // Fetch current credentials
    const {
        data: credentials,
        isLoading,
        error
    } = useQuery({
        queryKey: ["credentials"],
        queryFn: () => credentialsService.getCredentials(),
        retry: false,
    });

    // Update credentials mutation
    const updateMutation = useMutation({
        mutationFn: (data) => credentialsService.updateCredentials(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["credentials"]);
            alert("Informations d'entreprise mises à jour avec succès !");
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert("Erreur lors de la mise à jour des informations");
        },
    });

    // Initialize form when credentials are loaded
    useEffect(() => {
        if (credentials) {
            setFormData({
                companyName: credentials.companyName || "",
                address: credentials.address || "",
                phoneNumber: credentials.phoneNumber || "",
                taxId: credentials.taxId || ""
            });
        }
    }, [credentials]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateMutation.mutate(formData);
    };

    const handleReset = () => {
        if (credentials) {
            setFormData({
                companyName: credentials.companyName || "",
                address: credentials.address || "",
                phoneNumber: credentials.phoneNumber || "",
                taxId: credentials.taxId || ""
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    return (
        <div className="p-6">
            <TaleDashbaordMainCard className="p-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold">Paramètres de l'entreprise</h1>
                    <p className="text-gray-600">
                        Configurez les informations de votre entreprise pour les factures
                    </p>
                    {credentials?.timestamp && (
                        <span className="block text-sm text-gray-500 mt-1">
                            Dernière mise à jour: {new Date(credentials.timestamp).toLocaleDateString('fr-FR')}
                        </span>
                    )}
                    <hr />
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Company Information */}
                        <div className="space-y-4">
                            <TaleInput
                                label="Nom de l'entreprise *"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleInputChange}
                                required
                                placeholder="Ex: Votre Entreprise SARL"
                                maxLength={100}
                            />

                            <TaleInput
                                label="Identifiant Fiscal Unique (IFU) *"
                                name="taxId"
                                value={formData.taxId}
                                onChange={handleInputChange}
                                required
                                placeholder="Ex: 12345678/A/M/000"
                                maxLength={20}
                                helperText="Format tunisien: 8 chiffres suivis de /A/M/000"
                            />
                        </div>

                        {/* Contact Information */}
                        <div className="space-y-4">
                            <TaleInput
                                label="Adresse *"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                placeholder="Ex: Rue Habib Bourguiba, 1002 Tunis, Tunisie"
                                maxLength={200}
                            />

                            <TaleInput
                                label="Numéro de téléphone *"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                required
                                placeholder="Ex: +216 71 123 456"
                                maxLength={20}
                                helperText="Format international: +216 XX XXX XXX"
                            />
                        </div>
                    </div>

                    {/* Preview Section */}
                    <div className="mb-8 p-6 bg-gray-50 rounded-xl border">
                        <h3 className="text-lg font-semibold mb-4">Aperçu sur la facture</h3>
                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h4 className="text-xl font-bold text-gray-900">
                                        {formData.companyName || "[Nom de l'entreprise]"}
                                    </h4>
                                    <p className="text-gray-600 mt-1">
                                        {formData.address || "[Adresse de l'entreprise]"}
                                    </p>
                                    <div className="mt-2 text-sm text-gray-500">
                                        <p>Téléphone: {formData.phoneNumber || "[Téléphone]"}</p>
                                        <p>IFU: {formData.taxId || "[Identifiant Fiscal Unique]"}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-bold text-blue-600">FACTURE</div>
                                    <div className="text-sm text-gray-500 mt-1">#FAC-2024-001</div>
                                    <div className="text-xs text-gray-400 mt-1">Conforme à la législation tunisienne</div>
                                </div>
                            </div>

                            <div className="border-t pt-4 text-sm text-gray-500">
                                <p>Ces informations apparaîtront en en-tête de toutes vos factures.</p>
                                <p className="mt-1 text-xs text-gray-400">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    Conforme au Code de la TVA tunisien et à la réglementation fiscale
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Usage Information */}
                    <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <h3 className="text-lg font-semibold mb-3">Où ces informations apparaissent-elles ?</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-start">
                                    <i className="fas fa-file-invoice text-blue-500 mr-2 mt-1"></i>
                                    En-tête de toutes les factures
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-download text-green-500 mr-2 mt-1"></i>
                                    Documents PDF générés
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-envelope text-purple-500 mr-2 mt-1"></i>
                                    Emails de confirmation
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-print text-orange-500 mr-2 mt-1"></i>
                                    Version imprimable des factures
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-balance-scale text-red-500 mr-2 mt-1"></i>
                                    Déclarations fiscales tunisiennes
                                </li>
                            </ul>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <h3 className="text-lg font-semibold mb-3">Conseils pour la Tunisie</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                                    Utilisez le nom légal complet de votre entreprise
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                                    Incluez l'adresse complète avec gouvernorat
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                                    Vérifiez l'exactitude de votre IFU (8 chiffres)
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                                    Format téléphone: +216 XX XXX XXX
                                </li>
                                <li className="flex items-start">
                                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                                    Respectez les exigences du régime fiscal tunisien
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-4 border-t">
                        <div className="text-sm text-gray-600">
                            <p className="font-medium">Informations importantes pour la Tunisie:</p>
                            <ul className="list-disc pl-5 mt-1 space-y-1">
                                <li>Tous les champs marqués d'un * sont obligatoires pour la conformité fiscale</li>
                                <li>L'IFU doit être valide et correspondre à votre entreprise</li>
                                <li>Ces informations seront vérifiées par l'administration fiscale tunisienne</li>
                                <li>Assurez-vous que les informations sont exactes et à jour selon la loi tunisienne</li>
                            </ul>
                        </div>

                        <div className="flex gap-4">
                            <TaleButton
                                type="button"
                                variant="outlined"
                                onClick={handleReset}
                                disabled={updateMutation.isPending || !credentials}
                            >
                                <i className="fas fa-undo mr-2"></i>
                                Réinitialiser
                            </TaleButton>
                            <TaleButton
                                type="submit"
                                loading={updateMutation.isPending}
                            >
                                <i className="fas fa-save mr-2"></i>
                                Enregistrer les modifications
                            </TaleButton>
                        </div>
                    </div>
                </form>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default CredentialsSettings;
